import React, { Component, PropTypes } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { getPaymentOptionUserData, getUserValueFromState } from '../../../../redux/selectors/userAmounts';
import { updateDownPaymentActionApp, updateCashbackAmount } from '../../../../redux/actions/userAmounts';
import { updateBestOfferState, getBestOfferActionFromState, initialRenderCompleted } from '../../../../redux/actions/bestOffers';
import { openModalDetailsAction, closeModalDetailsAction } from '../../../../redux/actions/offer';
import { paymentTypes } from '../../../../redux/constants';
import { getTermFromState } from '../../../../redux/selectors/i18n';
import { formatCurrency, values, get } from '../../../../utils';
import BestOfferCard from '../BestOfferCard';
import { getAcquisitionFee, createFinanceOfferOptions } from '../../../../redux/selectors/bestOffer';
import { getMileageOptions } from '../../../../redux/selectors/paymentOptionCalculations';
import { getTotalPrice } from '../../../../redux/selectors/helios';
import { getActiveModel } from '../../../../redux/selectors/models'

/**
 * This function creates the default values for a specific option that
 * is to be displayed using the data that first comes in from the call.
 * @param state - the redux stroe
 * @param id - the offer specific id
 * @param paymentOption - The offer as a JSON object
 * @param offerType - Lease or Finance
 * @returns {*} - default store values
 */
const createDefaultPaymentOptionValues = (state, id, paymentOption, offerType) => {
  /* eslint-disable */
  const ISOFFER = paymentOption.theOfferData.isOffer;
  /* eslint-enable */
  let defaultTerm;
  let defaultMileage;
  let defaultOfferSelection;
  if (offerType === paymentTypes.FINANCING) {
    defaultTerm = paymentOption.term;
    defaultOfferSelection = '';
  } else if (offerType === paymentTypes.LEASE) {
    defaultTerm = paymentOption.term;
    defaultMileage = paymentOption.mileage;
  }
  const defaults = {
    isOfferApplied: ISOFFER,
    selectedTerm: defaultTerm,
    selectedMileage: defaultMileage,
    selectedOfferType: defaultOfferSelection,
    isOfferAppliedPending: false,
    fullOfferDetailsVisible: false
  };
  const existingValues = getPaymentOptionUserData(state, offerType);
  return Object.assign({}, defaults, existingValues)
};

/**
 * Creates the term options to be displayed in the select box
 * and maps them to a key for selection
 * @param paymentOption - the option/offer
 * @param selectedMileage - The pre-selected mileage to display
 * @param monthLabel - The labels for the different terms
 * @param isOfferApplied - if it is an offer or incentive
 * @returns {Array} - the array of options
 */
const createTerms = (paymentOption, selectedMileage, monthLabel, isOfferApplied) => {
  /* eslint-disable */
  const ISLEASE = paymentOption.paymentType === paymentTypes.LEASE;
  const ISFINANCE = paymentOption.paymentType === paymentTypes.FINANCING;
  /* eslint-enable */
  const isOffer = paymentOption.isOffer;
  let terms = [];
  if (ISFINANCE) {
    terms = values(paymentOption.theOfferData.terms)
      .filter((term) => {
        if (isOfferApplied === false) {
          return true;
        }
        const shouldFilter = !(
          isOffer === true && term.applicableToOffer === false
        )
        return shouldFilter;
      })
      .map(term => ({
        text: `${term.months} ${monthLabel}`,
        value: term.months
      }))
  } else if (ISLEASE) {
    terms = values(paymentOption.theOfferData.terms[selectedMileage]).map(term => ({
      text: `${term.months} ${monthLabel}`,
      value: term.months
    }))
  }
  return terms;
};

/**
 * Class to parse out the different values from the JSON object and pass those items
 * to the Card based on different factors and logic.
 */
class BestOfferProvider extends Component {
  static propTypes = {
    // REWORKING FOR USE WITH NEW BEST OFFER API
    isBest: PropTypes.string.isRequired, //eslint-disable-line
    offerType: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    updateUserValues: PropTypes.func.isRequired,
    modalDetailsOpen: PropTypes.func.isRequired,
    modalDetailsClose: PropTypes.func.isRequired,
    language: PropTypes.string.isRequired,
    brand: PropTypes.string.isRequired,
    paymentOption: PropTypes.object.isRequired,
    userValues: PropTypes.object.isRequired,
    terms: PropTypes.array.isRequired,
    mileage: PropTypes.array.isRequired,
    financeDropdownOptions: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    monthlyPayment: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    apr: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    acquisitionFee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    userTradeIn: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    userDownPayment: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    requiredInitialPayment: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    ribbonTitle: PropTypes.string,
    popularEquippedPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    cashBackAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    residualValue: PropTypes.number,
    estimatedAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    adjustedCost: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    dueAtSigning: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    selectedDropdownOption: PropTypes.string,
    leaseCashback: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    updateDownPayment: PropTypes.func.isRequired,
    updateCashback: PropTypes.func.isRequired,
    userDownPaymentBool: PropTypes.bool,
    bestOffers: PropTypes.func.isRequired,
    firstPageLoad: PropTypes.func.isRequired,
    firstRender: PropTypes.bool.isRequired,
    totalPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    modelLine: PropTypes.string.isRequired,
    promoTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  };
  static defaultProps = {
    acquisitionFee: 0,
    requiredInitialPayment: 0,
    offerMonthlyPayment: 0,
    defaultMonthlyPayment: 0,
    ribbonTitle: '',
    popularEquippedPrice: 0,
    residualValue: 0,
    estimatedAmount: 0,
    adjustedCost: 0,
    dueAtSigning: 0,
    userDownPaymentBool: false,
    financeDropdownOptions: [],
    selectedDropdownOption: '',
    leaseCashback: 0
  };
  constructor(props) {
    super(props);
    this.state = { firstRender: true };
    this.firstLoad = this.firstLoad.bind(this);
    this.update = this.update.bind(this);
    this.updateValues = this.updateValues.bind(this);
    this.changeTerm = this.changeTerm.bind(this);
    this.changeMileage = this.changeMileage.bind(this);
    this.viewOfferDetails = this.viewOfferDetails.bind(this);
    this.handleOfferModalClosed = this.handleOfferModalClosed.bind(this);
    this.changeFinanceOfferSelection = this.changeFinanceOfferSelection.bind(this);
  }
  componentDidMount() {
    const { paymentOption, userDownPaymentBool, firstRender } = this.props;
    if (paymentOption.paymentType === paymentTypes.LEASE && firstRender && userDownPaymentBool) { //eslint-disable-line
      this.firstLoad();
    }
  }
  componentDidUpdate() {
    const { onChange } = this.props;
    onChange();
  }

  // IF this is the first time loading then populate the down payment
  // box with the required down (not actually required just poorly
  // named in the data) and set the flag to false
  firstLoad() {
    const { paymentOption, updateDownPayment, bestOffers, firstPageLoad } = this.props;
    let customerDown = parseInt(paymentOption.theOfferData.customerDown, 10);
    if (!customerDown) {
      customerDown = 0;
    }
    updateDownPayment(customerDown);
    firstPageLoad();
    bestOffers();
    this.setState({ firstRender: false });
  }

  // Update a User value based on a key
  update(key, value) {
    const { id, updateUserValues, paymentOption, offerType } = this.props;
    const paymentType = offerType;
    const optionType = paymentOption.theOfferData.isOffer ? 'OFFER' : 'INCENTIVE';
    const target = {};
    target[key] = value;
    updateUserValues(id, target, paymentType, optionType);
  }

  // Update the USer Values object
  updateValues(object) {
    const { id, updateUserValues, paymentOption, offerType } = this.props;
    const paymentType = offerType;
    const optionType = paymentOption.isOffer ? 'OFFER' : 'INCENTIVE';
    updateUserValues(id, object, paymentType, optionType)
  }

  // Fires when the term is changed to reset cashback selection
  // and update term selection/ price
  changeTerm(term) {
    const { offerType } = this.props;
    if (offerType === 'FINANCE') {
      this.updateValues({
        selectedTerm: term.value,
      });
    } else {
      this.update('selectedTerm', term.value);
    }
  }

  // Fires when the mileage is changed to update the selection/price
  changeMileage(mileage) {
    // some logic in here to prevent users from changing options for applied lease offers
    const { paymentOption, userValues, offerType } = this.props;
    const { isOffer } = paymentOption;
    const { isOfferApplied } = userValues;
    if (isOffer === true && isOfferApplied && offerType === 'Lease') {
      this.updateValues({ isOfferAppliedPending: true });
    } else {
      this.update('selectedMileage', mileage.value);
    }
  }

  changeFinanceOfferSelection(offerSelection) {
    const { cashBackAmount, updateCashback } = this.props;
    updateCashback(cashBackAmount);
    this.updateValues({
      selectedOfferType: offerSelection.value,
      selectedFinanceDropdown: offerSelection.options[offerSelection.selectedIndex].text
    })
  }

  // Fires when the user clicks on the view special pricing link on a offer
  viewOfferDetails() {
    const { modalDetailsOpen } = this.props;
    modalDetailsOpen();
    this.updateValues({ isOfferAppliedPending: true });
  }

  // Fires when the view special pricing link closes (MKS)
  handleOfferModalClosed() {
    const { modalDetailsClose } = this.props;
    modalDetailsClose();
    this.updateValues({ isOfferAppliedPending: false });
  }

  // Builds the props for displaying the Offer card and passes them to it.
  buildPaymentOptionViewProps() {
    const {
      offerType,
      brand,
      language,
      paymentOption,
      userValues,
      monthlyPayment,
      terms,
      mileage,
      financeDropdownOptions,
      apr,
      acquisitionFee,
      userTradeIn,
      userDownPayment,
      requiredInitialPayment,
      ribbonTitle,
      popularEquippedPrice,
      cashBackAmount,
      estimatedAmount,
      adjustedCost,
      dueAtSigning,
      residualValue,
      selectedDropdownOption,
      leaseCashback,
      totalPrice,
      modelLine,
      promoTypeId
    } = this.props;
    const viewProps = {
      brand,
      language,
      ...userValues,
      title: offerType,
      paymentType: offerType,
      optionType: paymentOption.theOfferData.isOffer ? 'OFFER' : 'INCENTIVE',
      monthlyPayment: formatCurrency(monthlyPayment),
      aprValue: `${apr}%`,
      offerDescription: get(paymentOption.theOfferData, 'longDescription'),
      termOptions: terms,
      mileageOptions: mileage,
      financeDropdownOptions,
      viewOfferDetailsAction: this.viewOfferDetails,
      changeTermAction: this.changeTerm,
      changeMileageAction: this.changeMileage,
      changeOfferSelectionAction: this.changeFinanceOfferSelection,
      handleModalClosedAction: this.handleOfferModalClosed,
      acquisitionFee: formatCurrency(acquisitionFee),
      requiredInitialPayment: formatCurrency(requiredInitialPayment),
      isOpen: paymentOption.isOpen,
      ribbonTitle,
      popularEquippedPrice: formatCurrency(popularEquippedPrice),
      cashBackAmount: formatCurrency(cashBackAmount),
      estimatedAmount: formatCurrency(estimatedAmount),
      adjustedCost: formatCurrency(adjustedCost),
      dueAtSigning: formatCurrency(dueAtSigning),
      residualValue: Math.ceil(residualValue),
      selectedDropdownOption,
      apr,
      leaseCashback,
      userTradeIn: formatCurrency(userTradeIn),
      userDownPayment: formatCurrency(userDownPayment),
      totalPrice: totalPrice ? formatCurrency(totalPrice) : '',
      modelLine,
      promoTypeId
    };
    return viewProps;
  }
  render() {
    const viewProps = this.buildPaymentOptionViewProps(this.props);
    return <BestOfferCard {...viewProps} />
  }
}

/**
 * This function is pretty messy but the idea is that here we map all the many different
 * details into a JSON object to pass to the Offer Card to display all the values. Eavh
 * Card is self contained (mostly) and displays based on the data here
 * @param state - the redux store
 * @param ownProps
 * @returns {
 *   {
 *     brand: (*|string|string),
 *     language: (*|string|string),
 *     MonthlyPayment: *,
 *     terms: Array,
 *     mileage,
 *     cashBackOptions,
 *     userValues: *,
 *     apr: number,
 *     acquisitionFee: *,
 *     requiredInitialPayment: *,
 *     cashBackAmount: (string|string),
 *     popularEquippedPrice,
 *     estimatedAmount: *,
 *     adjustedCost: *,
 *     dueAtSigning,
 *     residualValue: *,
 *     selectedCashBack: string,
 *     leaseCashback: number,
 *     userTradeIn,
 *     userDownPayment,
 *     firstRender: (BestOfferProvider.firstLoad|boolean|(function(this:BestOfferProvider))|*),
 *     userDownPaymentBool: *
 *   }
 * }
 */
const mapStateToProps = (state, ownProps) => {
  // This entire function needs to be refactored, its a nightmare to read
  const { id, paymentOption, offerType } = ownProps;
  const userValues = createDefaultPaymentOptionValues(state, id, paymentOption, offerType);
  const modelLine = getActiveModel(state).modelLineCode;

  /* eslint-disable */
  const ISOFFER = paymentOption.theOfferData.isOffer;
  const ISLEASE = paymentOption.paymentType === paymentTypes.LEASE;
  /* eslint-enable */
  const selectedTerm = parseInt(userValues.selectedTerm, 10);
  const selectedMileage = userValues.selectedMileage;
  const monthLabel = getTermFromState(state, 'offerPanel.topPanel.termSelectorLabel');
  const terms = createTerms(paymentOption, selectedMileage, monthLabel, userValues.isOfferApplied); //eslint-disable-line
  const mileage = getMileageOptions(state, paymentOption.theOfferData);
  const financeDropdownOptions = createFinanceOfferOptions(state, selectedTerm);
  const apr = paymentOption.apr || 0;
  const popularEquippedPrice = paymentOption.popularEquippedPrice;
  const monthlyPayment = paymentOption.payment;
  const cashBackAmount = paymentOption.cashBack;
  const dueAtSigning = paymentOption.dueAtSigning;
  const estimatedAmount = paymentOption.adjustedCaptitalCost;
  const adjustedCost = paymentOption.adjustedCaptitalCost;
  const userTradeIn = getUserValueFromState(state, 'tradeIn');
  const userDownPayment = getUserValueFromState(state, 'downPayment');
  const firstRender = state.bestOffers.firstLoad;
  const promoTypeId = paymentOption.theOfferData.promoTypeId || '';
  const totalPrice = getTotalPrice(paymentOption) || '';
  let userDownPaymentBool;
  let acquisitionFee;
  let requiredInitialPayment;
  let residualValue;
  let selectedDropdownOption;

  /* Some values are only present on Lease Offers */
  if (ISLEASE) {
    userDownPaymentBool = state.userAmounts.defaultDownPayment;
    acquisitionFee = getAcquisitionFee(paymentOption, selectedTerm, selectedMileage);
    requiredInitialPayment = ISOFFER ? paymentOption.theOfferData.customerDown : undefined;
    residualValue = parseInt(paymentOption.percentage, 10);
  }
  if (!ISLEASE) {
    selectedDropdownOption = state.bestOffers.userValues.FINANCE.selectedOfferType || 'bestAPR';
  }
  return {
    brand: state.environment.BRAND_NAME,
    language: state.environment.LANG,
    monthlyPayment,
    terms,
    mileage,
    financeDropdownOptions,
    userValues,
    apr,
    acquisitionFee,
    requiredInitialPayment,
    cashBackAmount,
    popularEquippedPrice,
    estimatedAmount,
    adjustedCost,
    dueAtSigning,
    residualValue,
    selectedDropdownOption,
    userTradeIn,
    userDownPayment,
    firstRender,
    userDownPaymentBool,
    totalPrice,
    modelLine,
    promoTypeId
  }
};

const mapDispatchToProps = dispatch => ({
  updateUserValues: bindActionCreators(updateBestOfferState, dispatch),
  modalDetailsOpen: bindActionCreators(openModalDetailsAction, dispatch),
  modalDetailsClose: bindActionCreators(closeModalDetailsAction, dispatch),
  updateDownPayment: bindActionCreators(updateDownPaymentActionApp, dispatch),
  bestOffers: bindActionCreators(getBestOfferActionFromState, dispatch),
  firstPageLoad: bindActionCreators(initialRenderCompleted, dispatch),
  updateCashback: bindActionCreators(updateCashbackAmount, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(BestOfferProvider);



/** WEBPACK FOOTER **
 ** ./src/pages/TrimView/BestOfferList/BestOfferProvider/index.js
 **/